import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import type { PieSeriesOption } from 'echarts';
import {
  IBonusDataSegment,
  IBonusDataSegmentsParams,
  ICircularGraphData,
  ICircularGraphStatusTranslations,
  IColorMap,
  ILinearGradient,
  ISeriesDataItem,
  ITooltipParams,
  TCircularGraphNameOverrides,
  TCircularGraphStatusType,
} from './circular-graph.interface';

@Injectable()
export class CircularGraphService {
  private readonly numberSeparatorUtilitiesService = inject(NumberSeparatorUtilitiesService);
  private readonly translate = inject(TranslateService);

  private readonly defaultNameMap: ICircularGraphStatusTranslations = {
    bonus: this.translate.instant('components.circularGraph.bonus'),
    failed: this.translate.instant('components.circularGraph.failed'),
    problem: this.translate.instant('components.circularGraph.problem'),
    processing: this.translate.instant('components.circularGraph.processing'),
    remaining: this.translate.instant('components.circularGraph.remaining'),
    success: this.translate.instant('components.circularGraph.success'),
  } as const;

  public readonly nameMap: Record<TCircularGraphStatusType, ICircularGraphStatusTranslations> = {
    content: {
      ...this.defaultNameMap,
      failed: this.translate.instant('shared.statuses.missing'),
      processing: this.translate.instant('shared.statuses.planned'),
      success: this.translate.instant('shared.statuses.matched'),
    },
    default: { ...this.defaultNameMap },
    deliverable: {
      ...this.defaultNameMap,
      failed: this.translate.instant('shared.statuses.failed'),
      problem: this.translate.instant('shared.statuses.problem'),
      processing: this.translate.instant('shared.statuses.processing'),
      success: this.translate.instant('shared.statuses.ok'),
    },
    kpi: {
      ...this.defaultNameMap,
      failed: this.translate.instant('shared.statuses.kpiGoalsUnavailable'),
      problem: this.translate.instant('shared.statuses.kpiGoalsBelowTarget'),
      success: this.translate.instant('shared.statuses.kpiGoalsReached'),
    },
  };

  public readonly defaultColorMap: IColorMap = {
    bonus: '#ec8cff',
    failed: '#fc6a73',
    problem: '#ff9524',
    processing: '#415ba8',
    remaining: '#1f04241f',
    success: '#7ce286',
  };

  public buildMainSeriesData(
    data: ICircularGraphData,
    total: number,
    includeBonusInMainSeries = false,
  ): ISeriesDataItem[] {
    const seriesData: ISeriesDataItem[] = [];

    const processing = data.processing ?? 0;
    const success = data.success ?? 0;
    const failed = data.failed ?? 0;
    const problem = data.problem ?? 0;
    const bonus = includeBonusInMainSeries ? (data.bonus ?? 0) : 0;

    const sum = processing + success + failed + problem + bonus;
    const remaining = total - sum;

    if (processing > 0) {
      seriesData.push({ name: 'processing', value: processing });
    }

    if (success > 0) {
      seriesData.push({ name: 'success', value: success });
    }

    if (failed > 0) {
      seriesData.push({ name: 'failed', value: failed });
    }

    if (problem > 0) {
      seriesData.push({ name: 'problem', value: problem });
    }

    if (bonus > 0) {
      seriesData.push({ name: 'bonus', value: bonus });
    }

    if (remaining > 0) {
      seriesData.push({ name: 'remaining', value: remaining });
    }

    if (seriesData.length === 0 && total === 0) {
      seriesData.push({ name: 'remaining', value: 1 });
    }

    return seriesData;
  }

  public buildBonusSeriesData(bonus: number, total: number): ISeriesDataItem[] {
    if (bonus <= 0 || total <= 0) {
      return [];
    }

    const bonusRatio = bonus / total;
    const fullCircles = Math.floor(bonusRatio);
    const remainder = bonusRatio - fullCircles;

    const seriesData: ISeriesDataItem[] = [];

    for (let i = 0; i < fullCircles; i++) {
      seriesData.push({ name: 'bonus', value: total });
    }

    if (remainder > 0) {
      seriesData.push({ name: 'bonus', value: remainder * total });
    }

    return seriesData;
  }

  public buildMainSeries(
    data: ISeriesDataItem[],
    chartSize = 196,
    colorMap: Partial<IColorMap> = {},
  ): PieSeriesOption[] {
    if (data.length === 0) {
      return [];
    }

    const scale = chartSize / 196;
    const innerRadius = Math.round(56 * scale);
    const outerRadius = Math.round(84 * scale);

    const mergedColorMap = { ...this.defaultColorMap, ...colorMap };

    return [
      {
        animation: true,
        center: ['50%', '50%'],
        data: data.map((item) => ({
          itemStyle: {
            borderColor: '#f7f7f7',
            borderRadius: 4,
            borderWidth: 2,
            color: mergedColorMap[item.name as keyof IColorMap],
          },
          name: item.name,
          value: item.value,
        })),
        emphasis: {
          disabled: true,
        },
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        radius: [innerRadius, outerRadius],
        type: 'pie',
      } as PieSeriesOption,
    ];
  }

  public buildBonusSeries(data: ISeriesDataItem[], total: number, chartSize = 196): PieSeriesOption[] {
    if (data.length === 0) {
      return [];
    }

    const scale = chartSize / 196;
    const bonusInnerRadius = 83.5 * scale;
    const bonusOuterRadius = 96 * scale;

    let cumulativeAngle = 90;
    const allSeries: PieSeriesOption[] = [];
    const hasNoOverlap = data.length === 1;
    const isLastIndex = data.length - 1;

    data.forEach((item, index) => {
      const isLastOverlapping = index === isLastIndex && index > 0;
      const shouldHaveBorderRadius = hasNoOverlap || isLastOverlapping;
      const bonusRatio = item.value / total;
      const segmentAngle = bonusRatio * 360;
      const endAngle = cumulativeAngle + segmentAngle;
      const bonusData = this.buildBonusSegmentData(item.value, total, isLastOverlapping, endAngle);

      allSeries.push({
        animation: true,
        center: ['50%', '50%'],
        data: bonusData,
        emphasis: {
          disabled: true,
        },
        itemStyle: shouldHaveBorderRadius
          ? {
              borderRadius: 12,
            }
          : { borderColor: '#ec8cff' },
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        radius: [bonusInnerRadius, bonusOuterRadius],
        startAngle: 90,
        tooltip: {
          borderColor: '#ec8cff',
        },
        type: 'pie',
        z: 10 + index,
      } as PieSeriesOption);

      cumulativeAngle = endAngle;
    });

    return allSeries;
  }

  public buildBonusSegmentData(
    bonusValue: number,
    total: number,
    isLastOverlapping: boolean,
    endAngle: number,
  ): IBonusDataSegment[] {
    const bonusRatio = bonusValue / total;
    const bonusAngle = bonusRatio * 360;
    const tipAngle = isLastOverlapping ? 20 : 0;
    const mainBonusAngle = bonusAngle - tipAngle;
    const emptyAngle = 360 - bonusAngle;

    const bonusData = this.buildBonusDataSegments({
      emptyAngle,
      endAngle,
      isLastOverlapping,
      mainBonusAngle,
      tipAngle,
      total,
    });

    return bonusData;
  }

  private buildBonusDataSegments(params: IBonusDataSegmentsParams): IBonusDataSegment[] {
    const { emptyAngle, endAngle, isLastOverlapping, mainBonusAngle, tipAngle, total } = params;

    const bonusData: IBonusDataSegment[] = [
      {
        itemStyle: {
          color: this.defaultColorMap.bonus,
        },
        name: 'bonus',
        value: (mainBonusAngle / 360) * total,
      },
    ];

    if (isLastOverlapping) {
      const tipGradient = this.getTipGradient(endAngle);

      bonusData.push({
        itemStyle: {
          borderRadius: 12,
          color: tipGradient,
        },
        name: 'bonus-tip',
        value: (tipAngle / 360) * total,
      });
    }

    bonusData.push({
      itemStyle: {
        color: 'transparent',
      },
      name: 'bonus-empty',
      value: emptyAngle > 0 ? (emptyAngle / 360) * total : 0.001,
    });

    return bonusData;
  }

  private getTipGradient(endAngle: number): ILinearGradient {
    const gradientAngle = ((endAngle - 90) * Math.PI) / 180;
    const x = Math.cos(gradientAngle);
    const y = Math.sin(gradientAngle);

    return {
      colorStops: [
        { color: this.defaultColorMap.bonus, offset: 0 },
        { color: '#b84dd4', offset: 1 },
      ],
      type: 'linear',
      x: 0.5 - x * 0.5,
      x2: 0.5 + x * 0.5,
      y: 0.5 - y * 0.5,
      y2: 0.5 + y * 0.5,
    };
  }

  public getTooltipFormatter(
    params: unknown,
    bonus: number,
    total: number,
    statusType: TCircularGraphStatusType,
    nameOverrides: TCircularGraphNameOverrides = {},
  ): string {
    if (!params || typeof params !== 'object') {
      return '';
    }

    const param = params as ITooltipParams;

    if (!param.name || typeof param.value !== 'number') {
      return '';
    }

    const marker = param.marker ?? '';

    if (param.name === 'bonus' || param.name === 'bonus-tip' || param.name === 'bonus-empty') {
      const percentage = total > 0 ? Math.round((bonus / total) * 100) : 0;
      const bonusName = nameOverrides.bonus ?? this.nameMap[statusType].bonus;

      return `${marker}${this.escapeHtml(bonusName)}: ${bonus} / ${total} (${this.numberSeparatorUtilitiesService.formatNumberToLocaleString(percentage, null, 1, 1)}%)`;
    }

    const overrideKey = param.name as keyof TCircularGraphNameOverrides;
    const displayName =
      nameOverrides[overrideKey] ??
      this.nameMap[statusType][param.name as keyof ICircularGraphStatusTranslations] ??
      param.name;
    const actualValue = param.name === 'remaining' && total === 0 ? 0 : param.value;
    const percent = param.percent
      ? `${this.numberSeparatorUtilitiesService.formatNumberToLocaleString(param.percent, null, 1, 1)}%`
      : '';

    return `${marker}${this.escapeHtml(displayName)}: ${actualValue} / ${total}${percent ? ` (${percent})` : ''}`;
  }

  /**
   * Escapes HTML metacharacters in a string before it's interpolated into the tooltip's HTML
   * template. ECharts renders a string `formatter` return value as `innerHTML`, and `bonusName`/
   * `displayName` can originate from `nameOverrides` - real, backend-controlled entity names (e.g.
   * a user group or user name) rather than a fixed translation-key label. Left unescaped, a
   * maliciously-named entity could inject arbitrary markup/script into the tooltip DOM.
   */
  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
